import {
  Component,
  h,
  Element,
  State,
  Prop,
  Event,
  EventEmitter,
  Watch,
  Listen,
  Method,
  Host,
} from "@stencil/core"
import anime from "animejs"
import { KeyboardBehavior, DirectionX, DirectionY } from "./types"
import {
  selectReducer,
  getInitialState,
  getFormattedSelection,
  isKeyboardBehavior,
  isValidFilter,
  isFilterLongEnough,
  SelectState,
  SelectActions,
} from "./select-store"
import { bp } from "../../core/helpers/breakpoint-helpers"
import {
  StoreConnection,
  StateHandle,
  parseFunction,
  getPreviousFromList,
  getNextFromList,
  closest,
  getOverlayOutletEl,
  sortByAppearanceInDomTree,
  dispatchEvent,
  getScrollContainerEl,
  getScreenOrientationAngle,
  getAppearance,
  appendToOverlayOutlet,
  getScrollTopLeft,
  isTouchscreen,
  translate,
} from "../../core/helpers/webcomponent-helpers"
import { BackgroundTheme } from "../../core/types/types"

@Component({
  tag: "sdx-select",
  styleUrl: "select.scss",
  shadow: true,
})
export class Select {
  private store: StoreConnection<this, SelectState, SelectActions>
  private overlayOutletEl?: Element
  private scrollContainerEl?: Element
  private componentEl?: HTMLDivElement
  private wrapperEl?: HTMLDivElement
  private filterInputEl?: HTMLSdxInputElement
  private nonEditableInputEl?: HTMLSdxInputElement
  private focussedEl?: HTMLSdxSelectOptionElement
  private invokeSelectCallback: Function = () => null
  private invokeChangeCallback: Function = () => null
  private invokeFilterCallback: Function = () => null
  private invokeFocusCallback: Function = () => null
  private invokeBlurCallback: Function = () => null
  private isTabDown = false
  private componentDidLoadComplete = false
  private hadFilterInputElFocus = false
  private isReappending = false
  private animationDuration = 200
  private resizeTimer?: NodeJS.Timer
  private hasConsumerProvidedListEl = false
  private maxAutocompleteOptions

  private parsedFilterFunction: (
    optionEl: HTMLSdxSelectOptionElement,
    filter: string
  ) => boolean = () => true

  private visualViewportResizeHandler = () => {
    // triggered on virtual keyboard toggle
    const { listEl, isInsideDialog } = this.state.get()

    if (
      this.isClosedOrClosing() ||
      !listEl ||
      !visualViewport ||
      !isInsideDialog
    ) {
      return
    }

    const spaceMovedUpByiOS = window.innerHeight - visualViewport.height // visualViewport.offsetTop is always 0 on iPad
    const virtualKeyboardVisible = spaceMovedUpByiOS > 10 // event triggered before offsetTop is getting 0 on iPhone (but not on iPad)
    const listElRectTop = listEl.getBoundingClientRect().top

    if (virtualKeyboardVisible) {
      listEl.style.maxHeight = `${visualViewport.height - listElRectTop}px`
    } else {
      listEl.style.maxHeight = ""
    }
  }

  private static readonly maxAutocompleteOptionsMobile = 5
  private static readonly maxAutocompleteOptionsDesktop = 10
  private static readonly minSpaceToWindow = 24 // $baseline-3
  private static readonly selectHeight = 48 // $select-height

  // When the selection changes by setting `this.value = [ "one", "two", ... ]`,
  // the selected options will be updated - but syncing back to "this.value" is
  // necessary, as long as `this.value` is valid.
  // These flag control in which way the sync happens.
  private valueChangedInProgress = false
  private filterInputElValueChangedInProgress = false
  private selectionChangedInProgress = false

  // A "native" hidden DOM element that is submitted when the item is used in a
  // traditional form.
  // Only used when `keyboard-behavior="autocomplete"`. In all other cases,
  // the hidden DOM elements of each selected sdx-select-option are read
  // instead.
  private lightDOMHiddenFormInputEl?: HTMLInputElement
  private dialogEl?: HTMLSdxDialogElement
  private dialogContentEl?: HTMLSdxDialogContentElement

  private optionElsDidUpdate = false
  private lastScreenOrientationAngle = 0

  /**
   * Emitted whenever the user is selecting an option or typing while
   * `keyboard-behavior="autocomplete"`.
   * @event
   */
  private static eventInput = "input"

  /**
   * Emitted when the input gets focus.
   * Used when `keyboard-behavior="filter"` or `keyboard-behavior="autocomplete"`.
   * @event
   */
  // @ts-ignore just for documentation purposes
  private static eventFocus = "focus"

  /**
   * Emitted when the input loses focus.
   * Used when `keyboard-behavior="filter"` or `keyboard-behavior="autocomplete"`.
   * @event
   */
  // @ts-ignore just for documentation purposes
  private static eventBlur = "blur"

  @Element() public el!: HTMLSdxSelectElement

  @State() public state!: StateHandle<SelectState>

  @State() private focussed = false
  @State() private isChildOfSdxFilter = false

  /**
   * Text to be displayed when nothing is selected.
   */
  @Prop() public placeholder: string = ""

  /**
   * Enable multi select.
   */
  @Prop() public multiple: boolean = false

  /**
   * Will be written on the top of the sdx-select.
   */
  @Prop({ reflect: true }) public label?: string

  /**
   * Description text read by the screen reader.
   * Will be appended to the loading spinner when `loading="true"`.
   */
  @Prop() public srHint: string = ""

  /**
   * Disables the sdx-select.
   */
  @Prop() public disabled: boolean = false

  /**
   * Shows a loading spinner and disables the `sdx-select`. Useful for async
   * data fetching. It also makes sure that the `noMatchesFoundLabel` won't
   * show. Don't forget to set srHint accordingly.
   * It's best practice to wait about 1s before showing. For loading times
   * longer than 10s consider a skeleton instead.
   */
  @Prop() public loading: boolean = false

  /**
   * How the component should behave when the user types something on the
   * keyboard:
   * `"focus"` jumps to and focuses the option starting with the typed character.
   * `"filter"` lists only options (and optgroups) that match the entered filter.
   * `"autocomplete"` is similar to `"filter"`, but makes the component behave
   * more like an input field, e.g. the `.value` reflects the content of the
   * filter and there is no thumb to open or close.
   */
  @Prop() public keyboardBehavior: KeyboardBehavior = "focus"

  /**
   * Shows a search icon when `keyboard-behavior="autocomplete"`.
   */
  @Prop() public type?: "search"

  /**
   * Filter the options of the sdx-select by typing.
   * Shortcut for `keyboard-behavior="filter"`.
   * @deprecated use "keyboard-behavior" instead.
   */
  @Prop() public filterable: boolean = false

  /**
   * Maximum dropdown height (e.g. `300px`), supported units are `px` and `vh`.
   * If no unit is provided, `px` will be used.
   */
  @Prop() public maxHeight?: string | number

  /**
   * Callback when an option is selected (and the select is *not* in
   * `autocomplete` mode).
   * @deprecated use input event instead.
   */
  @Prop() public selectCallback?: ((selection: any[]) => void) | string

  /**
   * Callback when an option is selected (or something was typed, when
   * `keyboard-behavior="autocomplete"`).
   * @deprecated use input event instead.
   */
  @Prop() public changeCallback?: ((selection: any[]) => void) | string

  /**
   * Callback when the user changed the filter keyword.
   * Used when `keyboard-behavior="filter"`.
   * @deprecated use sdxfilter event instead.
   */
  @Prop() public filterCallback?: ((keyword: string) => void) | string

  /**
   * Callback when the input gets focus.
   * Used when `keyboard-behavior="filter"` or `keyboard-behavior="autocomplete"`.
   * @deprecated use focus event instead.
   */
  @Prop() public focusCallback?: (() => void) | string

  /**
   * Callback when the input loses focus.
   * Used when `keyboard-behavior="filter"` or `keyboard-behavior="autocomplete"`.
   * @deprecated use blur event instead.
   */
  @Prop() public blurCallback?: (() => void) | string

  /**
   * Label text for "No matches found".
   */
  @Prop() public noMatchesFoundLabel: string = translate(
    "No matches found...",
    this.el.lang
  )

  /**
   * Background color scheme.
   */
  @Prop() public backgroundTheme: BackgroundTheme = "light"

  /**
   * The value(s) of the currently selected option(s).
   * Please note that this is always an array, even without the `"multiple"`
   * attribute, for both getting and setting the value, like
   * `mySelect.value = [ "value1" ]`.
   * Note that when being used for setting the initial value, the "selected"
   * attribute on the `<sdx-select-option />` has
   * the higher priority.
   */
  @Prop({ mutable: true }) public value: any[] = []

  /**
   * Name parameter (useful when the component is used in a traditional HTML
   * form submit).
   */
  @Prop() public name?: string

  /**
   * Function that decides whether an option element matches a filter by
   * returning true or false. Defaults to a function that performs a simple
   * string match test on the option elements textContent property.
   * Used when `keyboard-behavior="filter"` or `keyboard-behavior="autocomplete"`.
   */
  @Prop() public filterFunction?:
    | ((optionEl: HTMLSdxSelectOptionElement, filter: string) => boolean)
    | string

  /**
   * How long a filter string needs to be at least to trigger the filter.
   * Minimum length is 1 (following the standard pattern for input fields using
   * `"autocomplete"` as they should support the user while typing).
   * Used when `keyboard-behavior="filter"` or `keyboard-behavior="autocomplete"`.
   */
  @Prop() public filterMinLength: number = 3

  /**
   * Placeholder text for "Type to filter...".
   */
  @Prop() public filterInputPlaceholder: string = "Type to filter..."

  /**
   * Set this to false to declare the component as invalid and use the
   * `validation-message` attribute to explain why.
   */
  @Prop() public valid?: boolean

  /**
   * Text that explains the validation status to the user.
   */
  @Prop() public validationMessage?: string

  /**
   * Marks the component as required (please note that this itself does not
   * handle validation &mdash; use the `valid` and `validation-message` for
   * that).
   */
  @Prop() public required: boolean = false

  /**
   * Allowed number of characters.
   * Used when `keyboard-behavior="autocomplete"`.
   */
  @Prop() public maxlength?: number

  /**
   * Focuses the input field as soon as it's rendered.
   * Used when `keyboard-behavior="autocomplete"`.
   */
  @Prop() public autofocus: boolean = false

  /**
   * Disable animations for testing.
   * @private
   */
  @Prop() public animated: boolean = true

  /**
   * Emitted when the user changed the filter keyword while
   * `keyboard-behavior="filter"`.
   */
  @Event() public sdxfilter!: EventEmitter<{ keyword: string }>

  @Watch("state")
  public stateChanged({}, prev: StateHandle<SelectState>) {
    const state = this.state.get()
    const prevState = prev.get()

    if (state.selection !== prevState.selection) {
      this.selectionChanged(state)
    }

    if (state.optionEls !== prevState.optionEls) {
      this.optionElsChanged(state)
    }

    if (state.filter !== prevState.filter) {
      this.filterChanged(state)
    }

    if (state.filterInputElValue !== prevState.filterInputElValue) {
      this.filterInputElValueChanged(state)
    }
  }

  @Watch("value")
  public valueChanged() {
    if (!this.componentDidLoadComplete) {
      return
    }

    // Update the filter keyword when "autocomplete" and stop doing anything else
    if (isKeyboardBehavior(this.state.get(), "autocomplete")) {
      const filter = this.value[0] || ""

      if (!this.filterInputElValueChangedInProgress) {
        this.store.dispatch({ type: "SET_FILTER", filter })

        this.valueChangedInProgress = true
        this.store.set("filterInputElValue", filter)
        this.valueChangedInProgress = false
      }

      this.updateHiddenFormInputEl()

      this.invokeChangeCallback(this.value)

      return
    }

    const { allValuesValid, validatedValues, optionEls } = this.getByValues(
      this.value
    )

    if (!allValuesValid) {
      this.value = validatedValues

      return
    }

    // Update the selection
    if (!this.selectionChangedInProgress) {
      // Set selection
      this.valueChangedInProgress = true
      this.store.set("selection", optionEls)
      this.valueChangedInProgress = false
    }

    // Invoke callbacks
    if (this.state.get().connected) {
      this.invokeChangeCallback(this.value)
      this.invokeSelectCallback(this.value)
    }
  }

  @Watch("filterable")
  public filterableChanged() {
    this.store.set("filterable", this.filterable)
  }

  @Watch("filterInputPlaceholder")
  public filterInputPlaceholderChanged() {
    this.store.set("filterInputElPlaceholder", this.filterInputPlaceholder)
  }

  @Watch("filterMinLength")
  public filterMinLengthChanged() {
    this.store.set("filterMinLength", this.filterMinLength)
  }

  @Watch("noMatchesFoundLabel")
  public noMatchesFoundLabelChanged() {
    this.store.set("noMatchesFoundLabel", this.noMatchesFoundLabel)
  }

  @Watch("selectCallback")
  public selectCallbackChanged() {
    this.setInvokeSelectCallback()
  }

  @Watch("changeCallback")
  public changeCallbackChanged() {
    this.setInvokeChangeCallback()
  }

  @Watch("filterCallback")
  public filterCallbackChanged() {
    this.setInvokeFilterCallback()
  }

  @Watch("focusCallback")
  public focusCallbackChanged() {
    this.setInvokeFocusCallback()
  }

  @Watch("blurCallback")
  public blurCallbackChanged() {
    this.setInvokeBlurCallback()
  }

  @Watch("placeholder")
  public placeholderChanged() {
    this.store.set("placeholder", this.placeholder)
    this.resetFilter()
  }

  @Watch("name")
  public nameChanged() {
    this.store.set("name", this.name)
    this.updateHiddenFormInputEl()
  }

  @Watch("filterFunction")
  public filterFunctionChanged() {
    this.setFilterFunction()
  }

  @Watch("multiple")
  public multipleChanged() {
    this.store.set("multiple", this.multiple)
  }

  @Watch("required")
  public requiredChanged() {
    this.store.set("required", this.required)
  }

  @Watch("keyboardBehavior")
  public keyboardBehaviorChanged() {
    this.store.set("keyboardBehavior", this.keyboardBehavior)
  }

  @Watch("loading")
  public loadingChanged() {
    this.store.set("loading", this.loading)
    this.showNoMatchesFoundIfNecessary()
  }

  /**
   * Emitted when users press enter key or click on search button when
   * `type="search"` is set.
   */
  @Event() public sdxsearch!: EventEmitter

  @Listen("keyup", { target: "window" })
  public onWindowKeyUp(e: KeyboardEvent) {
    if (e.key === "Tab") {
      this.isTabDown = false
    }
  }

  @Listen("focus")
  public onFocus() {
    // Only handle focus triggered by pressing the tab key
    if (!this.isTabDown) {
      return
    }

    this.focussed = true
  }

  @Listen("blur")
  public onBlur() {
    // Only handle blur triggered by pressing the tab key
    if (!this.isTabDown) {
      return
    }

    this.close()
  }

  // { capture: true } to make sure it's executed before requestToClose() which
  // needs this.focussed to be up to date
  @Listen("click", { target: "window", capture: true })
  @Listen("touchend", { target: "window", capture: true })
  public onWindowClick(e: MouseEvent) {
    this.focussed =
      e.composedPath().includes(this.el) ||
      e.composedPath().includes(this.state.get().listEl!)
  }

  @Listen("resize", { target: "window" })
  public async onWindowResize() {
    const { display } = this.state.get()

    if (display === "closed") {
      return
    }

    const dialogDisplay = await this.dialogEl?.getDisplay()
    const isInsideDialog = this.setIsInsideDialog()

    if (!isInsideDialog && dialogDisplay === "open") {
      this.close()
      return
    }

    const currentAngle = getScreenOrientationAngle()
    if (currentAngle !== this.lastScreenOrientationAngle) {
      // close in case of an orientation change
      this.lastScreenOrientationAngle = currentAngle
      this.close()
    } else {
      // in case of a desktop browser just recalculate the position
      this.positionListEl()
    }
  }

  @Listen("keydown", { target: "window" })
  public onKeyDown(e: KeyboardEvent) {
    if (e.key === "Tab") {
      this.isTabDown = true
    }

    // Only react to key events if the component is focussed
    if (!this.focussed) {
      return
    }

    switch (e.key) {
      // Open focussed select
      case " ":
        // Only listen to "space" keydown if the input element (for filterable) is not focussed,
        // otherwise the user wouldn't be able to type white spaces anymore.
        if (!this.hasFilterInputElFocus()) {
          e.preventDefault() // Prevent scrolling

          // For single select, it's possible to select with space
          if (this.isOpenOrOpening() && !this.multiple && this.focussedEl) {
            this.focussedEl.click()
          } else {
            this.toggle()
          }
        }

        break

      // Select currently focussed element
      case "Enter":
        if (this.focussedEl) {
          // Just act like the user has clicked on it
          this.focussedEl.click()
        }

        break

      // Focus previous element
      case "ArrowUp":
        e.preventDefault() // Prevent scrolling
        this.setFocussedEl("previous")

        if (this.hasVisibleOptionEls()) {
          this.open()
        }

        break

      // Focus next element
      case "ArrowDown":
        e.preventDefault() // Prevent scrolling
        this.setFocussedEl("next")

        if (this.hasVisibleOptionEls()) {
          this.open()
        }

        break

      default:
        const isAlphanumericKey = e.key.length === 1

        if (isAlphanumericKey) {
          // Only focus by letter if not filterable
          if (!this.isFilterable()) {
            this.setFocussedElByFirstLetter(e.key)
          }
        }
    }
  }

  /**
   * Returns the current selection.
   * @deprecated read the "value" prop instead.
   */
  @Method()
  public async getSelection(): Promise<any[]> {
    return this.value
  }

  /**
   * Toggles the sdx-select.
   */
  @Method()
  public toggle(): Promise<void> {
    return new Promise((resolve) => {
      const state = this.state.get()

      // When "autocomplete" is set, only open the menu on a certain input field value length
      if (isKeyboardBehavior(state, "autocomplete")) {
        if (
          isFilterLongEnough(state.filterInputElValue, this.filterMinLength)
        ) {
          this.open().then(resolve)
        } else {
          resolve()
        }

        return
      }

      if (this.isOpenOrOpening()) {
        this.close().then(resolve)
      } else if (this.isClosedOrClosing()) {
        this.open().then(resolve)
      } else {
        resolve()
      }
    })
  }

  /**
   * Opens the select.
   */
  @Method()
  public open(): Promise<void> {
    return new Promise((resolve) => {
      const state = this.state.get()
      const { listEl } = state

      if (!listEl) {
        return
      }

      // Only a closed select can be opened
      if (!this.isClosedOrClosing()) {
        resolve()
        return
      }

      this.overlayOutletEl = getOverlayOutletEl()
      this.scrollContainerEl = getScrollContainerEl(this.el)

      // Check if the select is inside a scrollable element.
      // If yes, append the options there to make sure they scroll and aren't
      // detached from the wrapperEl.
      if (this.scrollContainerEl) {
        this.overlayOutletEl = this.scrollContainerEl
      } else {
        this.scrollContainerEl = document.documentElement
      }

      const isInsideDialog = this.setIsInsideDialog()

      if (isInsideDialog) {
        this.appendTo("dialogEl")
      } else {
        // Make options overlap all other content
        this.appendTo("overlayOutlet")
      }

      // Set dimensions
      this.positionListEl()

      this.store.set("display", "opening")

      anime({
        targets: listEl,
        scaleY: 1,
        opacity: 1,
        scrollTop: 0, // ensure scrollbar position in chrome
        duration: this.getAnimationDuration(),
        easing: "cubicBezier(0.550, 0.085, 0.320, 1)",
        complete: () => {
          this.store.set("display", "open")

          if (listEl) {
            listEl.style.transform = ""
          }

          resolve()
        },
      })
    })
  }

  /**
   * Closes the sdx-select.
   */
  @Method()
  public close(): Promise<void> {
    return new Promise(async (resolve) => {
      const state = this.state.get()

      if (state.display !== "open") {
        resolve()
        return
      }

      this.dialogEl?.close()
      this.setFocussedEl(null)

      // Shift focus from input field back to the sdx-select (if filterable).
      // Only an opened Select should be filterable.
      if (isKeyboardBehavior(state, "filter")) {
        this.nonEditableInputEl!.doFocus()
      }

      this.store.set("display", "closing")

      anime({
        targets: state.listEl,
        scaleY: 0,
        opacity: 0.2,
        duration: this.getAnimationDuration(),
        easing: "cubicBezier(0.550, 0.085, 0.320, 1)",
        complete: () => {
          if (isKeyboardBehavior(state, "filter")) {
            this.nonEditableInputEl!.doFocus()
          }

          this.store.set("display", "closed")

          // Bring options back to the component
          this.appendTo("component")

          state.listEl!.style.display = "none"

          resolve()
        },
      })
    })
  }

  /**
   * Sets the focus on the input field when `keyboard-behavior="filter"` or
   * `keyboard-behavior="autocomplete"`.
   */
  @Method()
  public async doFocus() {
    const { listEl } = this.state.get()
    this.filterInputEl?.doFocus() || listEl?.doFocus()
  }

  /**
   * @private
   * Calls close(), but only when the component is allowed to be closed.
   */
  @Method()
  public async requestToClose(
    e: KeyboardEvent | MouseEvent | TouchEvent
  ): Promise<void> {
    const { listEl } = this.state.get()

    if (!listEl) {
      return
    }

    if (e instanceof KeyboardEvent) {
      if (e.key === "Escape") {
        this.close()
      }

      return
    }

    const didClickOnEl = e.composedPath().includes(this.el)
    const didClickOnListEl = e.composedPath().includes(listEl)

    if (didClickOnEl || didClickOnListEl) {
      return
    }

    // Clicked somewhere else on window
    this.close()
  }

  constructor() {
    this.store = new StoreConnection(this, selectReducer, getInitialState(), [
      "selection",
      "listEl",
      "optionEls",
      "optgroupEls",
      "filter",
      "filterInputElValue",
      "directionX",
      "directionY",
      "display",
      "isInsideDialog",
    ])

    this.setInvokeSelectCallback()
    this.setInvokeChangeCallback()
    this.setInvokeFilterCallback()
    this.setInvokeFocusCallback()
    this.setInvokeBlurCallback()
    this.setFilterFunction()

    this.store.set("placeholder", this.placeholder)
    this.store.set("multiple", this.multiple)
    this.store.set("required", this.required)
    this.store.set("keyboardBehavior", this.keyboardBehavior)
    this.store.set(
      "animationDuration",
      this.animated ? this.animationDuration : 0
    )
    this.store.set("close", this.close.bind(this))
    this.store.set("filterable", this.filterable)
    this.store.set("filterMinLength", this.filterMinLength)
    this.store.set("noMatchesFoundLabel", this.noMatchesFoundLabel)
    this.store.set("filterInputElPlaceholder", this.filterInputPlaceholder)
    this.store.set("name", this.name)
    this.store.set("loading", this.loading)
    this.setIsInsideDialog()

    this.store.flush()

    this.maxAutocompleteOptions = bp("lg")
      ? Select.maxAutocompleteOptionsDesktop
      : Select.maxAutocompleteOptionsMobile

    this.updateHiddenFormInputEl()
    visualViewport?.addEventListener("resize", this.visualViewportResizeHandler)
  }

  public connectedCallback() {
    this.store.set("connected", true)

    this.isChildOfSdxFilter = !!closest(this.el, "sdx-filter")

    // The component is being reconnected
    if (this.isReappending) {
      this.store.set("reappending", true)
      this.isReappending = false
    }
  }

  public disconnectedCallback() {
    this.store.set("connected", false)

    // Know that a disconnection happened, maybe because the component was
    // removed, cached or it's being appended to document.body.
    const isStillInDocument = closest(this.el, document.documentElement)

    if (isStillInDocument) {
      this.isReappending = true
    } else {
      visualViewport?.removeEventListener(
        "resize",
        this.visualViewportResizeHandler
      )
    }

    // Make sure the component is closed when removed from the DOM
    this.close()
  }

  public componentWillLoad() {
    if (this.state.get().listEl) {
      this.hasConsumerProvidedListEl = true
    }
  }

  public componentDidLoad() {
    const { selection, listEl } = this.state.get()
    this.lastScreenOrientationAngle = getScreenOrientationAngle()

    // Now that all children are loaded, sort them now and from now on.
    // Having them sorted from the very beginning makes long lists very slow
    // on Chrome 97 and Safari 15 (but interestingly not on Firefox 95).
    this.store.dispatch({ type: "ACTIVATE_SORT" })

    // Initial selection:
    // If there are options with the "selected" prop, use those.
    // If not, check if there's a "value" and use that instead.
    if (selection.length) {
      const { validatedValues } = this.getByValues(
        selection.map((el) => el.value)
      )

      this.value = validatedValues
    } else if (this.value.length) {
      // use "value", if any
      const { allValuesValid, validatedValues, optionEls } = this.getByValues(
        this.value
      )

      if (allValuesValid && validatedValues.length) {
        this.store.set("selection", optionEls)
      }
    } else {
      // fall back by selecting first option
      this.selectFirstOptionElIfNecessary()
    }

    // List element is initially closed
    listEl!.style.display = "none"
    listEl!.style.opacity = ".2"
    listEl!.style.transform = "scaleY(0)"

    // Can't simply forward autocomplete={this.autocomplete} because sdx-input
    // is a child of sdx-select and therefore not yet visible, even within its
    // componentDidLoad(). Browsers don't allow focussing invisible elements.
    // Hence this manual call:
    if (this.autofocus) {
      this.doFocus()
    }

    this.store.subscribe()

    // All children are now ready
    this.componentDidLoadComplete = true
  }

  public componentDidUpdate() {
    if (this.optionElsDidUpdate) {
      // Apply filter to dynamically added options.
      // This has to happen after render() because the default filter function
      // uses .textContent which doesn't exist before render() in Angular when
      // using {{ text interpolation }}.
      this.showNoMatchesFoundIfNecessary()

      this.optionElsDidUpdate = false
    }
  }

  private selectionChanged(state: SelectState) {
    this.selectionChangedInProgress = true

    this.selectFirstOptionElIfNecessary()

    // Update state after possible first selection from above
    state = this.state.get()

    // Update the "value"
    if (!this.valueChangedInProgress) {
      const { validatedValues } = this.getByValues(
        state.selection.map((el) => el.value)
      )

      this.value = validatedValues

      if (state.userInteractionInProgress) {
        dispatchEvent(this.el, Select.eventInput)

        this.store.set("userInteractionInProgress", false)
      }
    }

    this.selectionChangedInProgress = false
  }

  private optionElsChanged(state: SelectState) {
    this.optionElsDidUpdate = true

    // If new options come in while the component is open, directly append them
    // to the body
    if (state.optionEls.length && this.isOpenOrOpening()) {
      this.appendTo("overlayOutlet")
    }
  }

  private filterChanged(state: SelectState) {
    this.showNoMatchesFoundIfNecessary()

    if (isKeyboardBehavior(state, "filter")) {
      this.invokeFilterCallback(state.filter)
      this.sdxfilter.emit({ keyword: state.filter })
    }
  }

  private filterInputElValueChanged(state: SelectState) {
    this.filterInputElValueChangedInProgress = true

    if (isKeyboardBehavior(state, "autocomplete")) {
      this.value = [state.filterInputElValue]

      if (state.userInteractionInProgress) {
        dispatchEvent(this.el, Select.eventInput)
      }
    }

    this.filterInputElValueChangedInProgress = false
  }

  // Appends all options to the body to guarantee visibility (as overlay)
  private async appendTo(target: "overlayOutlet" | "component" | "dialogEl") {
    const { display, listEl } = this.state.get()

    this.store.set("reappending", true)

    switch (target) {
      case "overlayOutlet":
        // When the component is already open, the list element is already
        // appended to the body. If it's appended twice, the filter input field
        // loses focus.
        if (display === "closed") {
          appendToOverlayOutlet(listEl!, this.overlayOutletEl!, this.el)
        }

        if (!this.hasConsumerProvidedListEl) {
          listEl!.append(...this.getDirectSlotEls())
        }

        break

      case "dialogEl":
        appendToOverlayOutlet(listEl!, this.dialogContentEl!, this.el)

        if (!this.hasConsumerProvidedListEl) {
          listEl!.append(...this.getDirectSlotEls())
        }

        // store set reappending=false after all options have been reappended once
        this.store.set("reappending", true)
        await this.dialogEl?.open()

        break

      default:
        // "component"
        if (this.hasConsumerProvidedListEl) {
          appendToOverlayOutlet(listEl!, this.el, this.el)
        } else {
          this.el.append(...this.getDirectSlotEls())
          appendToOverlayOutlet(listEl!, this.wrapperEl!, this.el)
        }
    }

    this.store.set("reappending", false)
  }

  private updateHiddenFormInputEl(): void {
    if (!isKeyboardBehavior(this.state.get(), "autocomplete")) {
      return
    }

    if (!this.lightDOMHiddenFormInputEl) {
      this.lightDOMHiddenFormInputEl = document.createElement("input")
      this.lightDOMHiddenFormInputEl.type = "hidden"
      this.el.append(this.lightDOMHiddenFormInputEl)
    }

    // Clean up properties added previously in case they've changed
    this.lightDOMHiddenFormInputEl.removeAttribute("name")
    this.lightDOMHiddenFormInputEl.removeAttribute("value")

    if (this.name) {
      this.lightDOMHiddenFormInputEl.name = this.name
      this.lightDOMHiddenFormInputEl.value = this.value[0] || ""
    }
  }

  // Resets the whole filter system
  private resetFilter(): void {
    this.resetFilterInputEl()
    this.store.dispatch({ type: "SET_FILTER", filter: "" })
  }

  // Parses and injects the "filter-function" prop into the store
  private setFilterFunction(): void {
    this.parsedFilterFunction = this.optionElMatchesFilter
  }

  // Write the current selection into the filter input field
  private resetFilterInputEl() {
    if (!this.isFilterable()) {
      return
    }

    const state = this.state.get()
    let filterInputElValue = ""

    // For "autocomplete", the filter input field should contain the selected value
    if (isKeyboardBehavior(state, "autocomplete")) {
      filterInputElValue = getFormattedSelection(state.selection)
    }

    this.store.set("filterInputElValue", filterInputElValue)
  }

  private positionListEl(): void {
    const { listEl, isInsideDialog } = this.state.get()

    if (
      !(this.componentEl && this.wrapperEl && this.scrollContainerEl && listEl)
    ) {
      return
    }

    if (isInsideDialog) {
      // due to the specificity style must be set here
      listEl.style.display = "block"
      listEl.style.minWidth = ""
      listEl.style.maxHeight = ""
      listEl.style.inset = ""
      listEl.style.transformOrigin = ""

      // stop calculating position because listEl is appended to dialogEl
      return
    }

    const wrapperElRect = this.wrapperEl.getBoundingClientRect()

    let spaceTowards = {
      bottom: document.documentElement.clientHeight - wrapperElRect.bottom,
      left: wrapperElRect.left,
      right: document.documentElement.clientWidth - wrapperElRect.right,
      top: wrapperElRect.top,
    }

    // Height calculation
    const maxHeightFromProp = this.getMaxHeight()
    listEl.style.maxHeight = maxHeightFromProp || "none"

    let scrollContainerElRect = {
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
    }

    // If the overlay is inside an offset parent, include all its spacings
    // in the calculation.
    if (this.scrollContainerEl !== document.documentElement) {
      scrollContainerElRect = this.scrollContainerEl.getBoundingClientRect()

      // Subtract wrapperElRect from scrollContainerElRect
      spaceTowards = {
        bottom: scrollContainerElRect.bottom - wrapperElRect.bottom,
        left: wrapperElRect.left - scrollContainerElRect.left,
        right: scrollContainerElRect.right - wrapperElRect.right,
        top: wrapperElRect.top - scrollContainerElRect.top,
      }
    }

    // Show the listEl to take measurements
    listEl.style.display = "block"

    let listElWidth = listEl.clientWidth
    let listElHeight = listEl.clientHeight
    let maxHeight = listEl.style.maxHeight

    // For "autocomplete", clientHeight is always 0, because it doesn't display
    // any options initially. Since that will falsify the positioning
    // calculation, act like the fully allowed number of options is displayed.
    if (isKeyboardBehavior(this.state.get(), "autocomplete")) {
      listElHeight = this.maxAutocompleteOptions * Select.selectHeight
    }

    let directionX: DirectionX
    let directionY: DirectionY

    if (spaceTowards.right - Select.minSpaceToWindow >= listElWidth) {
      // enough space towards right
      directionX = "right"
    } else if (spaceTowards.left - Select.minSpaceToWindow >= listElWidth) {
      // enough space towards left
      directionX = "left"
    } else if (spaceTowards.left > spaceTowards.right) {
      // not enough space, open towards left
      directionX = "left"
    } else {
      // not enough space, open towards right
      directionX = "right"
    }

    const heightOfTwoOptions = Select.selectHeight * 2

    if (
      spaceTowards.bottom - Select.minSpaceToWindow - heightOfTwoOptions >=
      listElHeight
    ) {
      // enough space towards bottom
      maxHeight = maxHeightFromProp
        ? maxHeight
        : `${spaceTowards.bottom - Select.minSpaceToWindow}px`
      directionY = "bottom"
    } else if (
      spaceTowards.top - Select.minSpaceToWindow - heightOfTwoOptions >=
      listElHeight
    ) {
      // enough space towards top
      maxHeight = maxHeightFromProp
        ? maxHeight
        : `${spaceTowards.top - Select.minSpaceToWindow}px`
      directionY = "top"
    } else if (spaceTowards.top > spaceTowards.bottom) {
      // not enough space, open towards top
      maxHeight = `${spaceTowards.top - Select.minSpaceToWindow}px`
      directionY = "top"
    } else {
      // not enough space, open towards bottom
      maxHeight = `${spaceTowards.bottom - Select.minSpaceToWindow}px`
      directionY = "bottom"
    }

    // There should always be a max-height, because even though it has enough
    // space at the time being, it might be that it grows dynamically while
    // being opened, for example by clearing a filter (which will show all
    // options immediately).
    listEl.style.maxHeight = maxHeight

    // List should never be smaller than its wrapper
    listEl.style.minWidth = `${wrapperElRect.width}px`

    if (directionX === "right") {
      listEl.style.right = "auto"
      listEl.style.left = `${
        getScrollTopLeft(this.scrollContainerEl).scrollLeft -
        scrollContainerElRect.left +
        wrapperElRect.left
      }px`
    } else {
      // "left"
      listEl.style.left = "auto"
      listEl.style.right = `${
        this.scrollContainerEl.clientWidth -
        getScrollTopLeft(this.scrollContainerEl).scrollLeft +
        scrollContainerElRect.left -
        wrapperElRect.right
      }px`
    }

    if (directionY === "bottom") {
      listEl.style.transformOrigin = "50% 0"
      listEl.style.bottom = "auto"
      listEl.style.top = `${
        getScrollTopLeft(this.scrollContainerEl).scrollTop -
        scrollContainerElRect.top +
        wrapperElRect.bottom -
        1
      }px`
      // -1px = border overlap
    } else {
      // "top"
      listEl.style.transformOrigin = "0 100%"
      listEl.style.top = "auto"
      listEl.style.bottom = `${
        this.scrollContainerEl.clientHeight -
        getScrollTopLeft(this.scrollContainerEl).scrollTop +
        scrollContainerElRect.top -
        wrapperElRect.top -
        1
      }px`
      // -1px = border overlap
    }

    // Propagate newly calculated direction
    this.store.set("directionX", directionX)
    this.store.set("directionY", directionY)
  }

  // If no "filter-function" is present, fall back to this
  private defaultFilterFunction(
    optionEl: HTMLSdxSelectOptionElement,
    filter: string
  ): boolean {
    // Skip placeholder and empty options
    if ((filter && optionEl.placeholder) || !optionEl.textContent) {
      return false
    }

    return optionEl.textContent.toLowerCase().indexOf(filter.toLowerCase()) > -1
  }

  /**
   * Returns true if an option element matches the filter (e.g. in "ca" in
   * "Car").
   * @param el The option to be tested.
   * @param filter The Filter to be tested.
   */
  private optionElMatchesFilter(
    el: HTMLSdxSelectOptionElement,
    filter: string
  ): boolean {
    let filterFunction = this.defaultFilterFunction

    // If a "filter-function" prop has been passed, use this instead
    if (this.filterFunction) {
      filterFunction = parseFunction(this.filterFunction) as () => boolean
    }

    let match = filterFunction(el, filter)

    // When "autocomplete", all options should be hidden if there's no filter,
    // while when "filter", all options should be shown if there's no filter.
    if (isKeyboardBehavior(this.state.get(), "autocomplete") && !filter) {
      match = false
    }

    return match
  }

  private setFocussedEl(
    which: HTMLSdxSelectOptionElement | null | "previous" | "next"
  ): void {
    const { optionEls, selection } = this.state.get()

    // First, unfocus all
    for (let i = 0; i < optionEls.length; i++) {
      const optionEl = optionEls[i]

      optionEl.classList.remove("focus")
    }

    // If no element is to be selected, clear and abort
    if (which === null) {
      delete this.focussedEl
      return
    }

    if (which === "previous" || which === "next") {
      const lastSelectedEl = selection[selection.length - 1]

      // If there's no currently focussed element, start from the last selected element
      let focussedEl = this.focussedEl || lastSelectedEl

      // If there's a focussed element, but is hidden, start from the first option
      if (focussedEl?.style.display === "none") {
        focussedEl = optionEls[0]
      }

      if (which === "previous") {
        let prevEl = getPreviousFromList(optionEls, focussedEl)
        let count = 0

        while (
          prevEl !== focussedEl &&
          (prevEl.disabled || prevEl.style.display === "none") &&
          count < optionEls.length
        ) {
          prevEl = getPreviousFromList(optionEls, prevEl)
          count += 1
        }

        this.focussedEl = prevEl
      } else {
        // "next"
        let nextEl = getNextFromList(optionEls, focussedEl)
        let count = 0

        while (
          nextEl !== focussedEl &&
          (nextEl.disabled || nextEl.style.display === "none") &&
          count < optionEls.length
        ) {
          nextEl = getNextFromList(optionEls, nextEl)
          count += 1
        }

        this.focussedEl = nextEl
      }
    } else {
      // "which" is an element
      this.focussedEl = which
    }

    if (this.focussedEl) {
      this.focussedEl.classList.add("focus")
      this.scrollToOption(this.focussedEl)
    }
  }

  /**
   * Scrolls the list the way that an option is visible in the center.
   */
  private scrollToOption(option: HTMLSdxSelectOptionElement) {
    const parent = this.state.get().listEl!
    const optionRect = option.getBoundingClientRect()
    const parentRect = parent.getBoundingClientRect()
    const isFullyVisible =
      optionRect.top >= parentRect.top &&
      optionRect.bottom <= parentRect.top + parent.clientHeight

    if (!isFullyVisible) {
      parent.scrollTop = optionRect.top + parent.scrollTop - parentRect.top
    }
  }

  /**
   * Returns all options starting with a certain letter.
   * @param letter Key value to look for.
   */
  private getOptionElsByFirstLetter(
    letter: string
  ): HTMLSdxSelectOptionElement[] {
    const { optionEls } = this.state.get()
    const results: HTMLSdxSelectOptionElement[] = []

    for (let i = 0; i < optionEls.length; i++) {
      const option = optionEls[i]

      if (
        option.textContent?.toLowerCase().charAt(0) === letter.toLowerCase()
      ) {
        results.push(option)
      }
    }

    return results
  }

  /**
   * Sets the focussed option starting by a given letter.
   * @param letter Key value to look for.
   */
  private setFocussedElByFirstLetter(letter: string): void {
    const optionsByFirstLetter = this.getOptionElsByFirstLetter(letter)

    if (optionsByFirstLetter.length) {
      let startIndex = 0

      if (this.focussedEl) {
        const focussedElIndex = optionsByFirstLetter.indexOf(this.focussedEl)

        if (focussedElIndex > -1) {
          startIndex = focussedElIndex
        }
      }

      let option: HTMLSdxSelectOptionElement | null =
        optionsByFirstLetter[startIndex]

      if (option.disabled || option === this.focussedEl) {
        for (let i = 0; i < optionsByFirstLetter.length; i++) {
          option = getNextFromList(
            optionsByFirstLetter,
            optionsByFirstLetter[startIndex]
          )

          if (option.disabled) {
            option = null
          } else {
            break
          }

          // Look behind
          if (startIndex < optionsByFirstLetter.length) {
            startIndex += 1
          } else {
            startIndex = 0
          }
        }
      }

      if (option) {
        this.setFocussedEl(option)
      }
    }
  }

  private setInvokeSelectCallback(): void {
    this.invokeSelectCallback = parseFunction(this.selectCallback)
  }

  private setInvokeChangeCallback(): void {
    this.invokeChangeCallback = parseFunction(this.changeCallback)
  }

  private setInvokeFilterCallback(): void {
    this.invokeFilterCallback = parseFunction(this.filterCallback)
  }

  private setInvokeFocusCallback(): void {
    this.invokeFocusCallback = parseFunction(this.focusCallback)
  }

  private setInvokeBlurCallback(): void {
    this.invokeBlurCallback = parseFunction(this.blurCallback)
  }

  /**
   * Checks if an array of values is valid and create a valid version of it.
   * For convenience, all options matching the values will also be returned.
   */
  private getByValues(values: any[]): {
    allValuesValid: boolean
    validatedValues: any[]
    optionEls: HTMLSdxSelectOptionElement[]
  } {
    if (Array.isArray(values)) {
      if (!values.length) {
        // Nothing to do, don't replace [] with [] because of change detection
        return { allValuesValid: true, validatedValues: values, optionEls: [] }
      }

      // Filter out undefined values
      const definedValues = values.filter((value) => value !== undefined)

      if (!definedValues.length) {
        // Either only undefined values or completely empty
        return { allValuesValid: false, validatedValues: [], optionEls: [] }
      }

      const validatedValues: any[] = []
      const foundOptionEls: HTMLSdxSelectOptionElement[] = []
      const { optionEls } = this.state.get()

      let isValid = true

      // Check if values exist in the options
      for (let i = 0; i < definedValues.length; i++) {
        const value = definedValues[i]
        const foundOptionEl = optionEls.find((el) => el.value === value)

        if (foundOptionEl) {
          if (this.multiple || (!this.multiple && i === 0)) {
            foundOptionEls.push(foundOptionEl)
            validatedValues.push(foundOptionEl.value)
          } else {
            // Single select should only respect one value
            isValid = false
          }
        }
      }

      if (validatedValues.length === 0) {
        // no values found
        return {
          allValuesValid: false,
          validatedValues,
          optionEls: foundOptionEls,
        }
      }

      return {
        allValuesValid: isValid,
        validatedValues,
        optionEls: foundOptionEls,
      }
    }

    // All non-array types will be reset (to an empty array)
    return { allValuesValid: false, validatedValues: [], optionEls: [] }
  }

  // If there are options, but no selection, and no placeholder,
  // fall back by selecting first option
  private selectFirstOptionElIfNecessary(): void {
    const { optionEls, selection, placeholder, multiple, keyboardBehavior } =
      this.state.get()

    if (
      optionEls.length &&
      !selection.length &&
      !placeholder &&
      !multiple &&
      keyboardBehavior !== "autocomplete"
    ) {
      const optionsNotDisabled = optionEls.filter((option) => !option.disabled)

      if (optionsNotDisabled.length === 0) {
        return
      }

      this.store.set("selection", [optionsNotDisabled[0]])
    }
  }

  private showNoMatchesFoundIfNecessary(): void {
    const { optionEls, optgroupEls, filter, selection } = this.state.get()
    let matches = 0

    optionEls.forEach((optionEl) => {
      let display

      if (this.parsedFilterFunction(optionEl, filter)) {
        matches += 1
        display = ""
      } else {
        display = "none"
      }

      // For "autocomplete", display only a certain number of option elements (for UX)
      if (isKeyboardBehavior(this.state.get(), "autocomplete")) {
        if (matches > this.maxAutocompleteOptions) {
          display = "none"
        }
      }

      optionEl.style.display = display
    })

    optgroupEls.forEach((optgroupEl) => {
      let match = false

      for (let optionEl of optgroupEl.querySelectorAll("sdx-select-option")) {
        if (optionEl.style.display === "") {
          match = true
          break
        }
      }

      optgroupEl.style.display = match ? "" : "none"
    })

    let noMatchesFound =
      isValidFilter(filter, selection, this.filterMinLength) &&
      matches === 0 &&
      !this.loading

    this.store.set("noMatchesFound", noMatchesFound)
  }

  // Returns optionEls and optgroupEls directly descending from <sdx-select>,
  // sorted by their appearance in the DOM tree to avoid having all options
  // first and all optgroups last.
  private getDirectSlotEls() {
    const { optionEls, optgroupEls } = this.state.get()

    return [
      ...optionEls.filter(
        (
          el // skip placeholder and options within optgroups
        ) => !el.placeholder && !closest(el, "sdx-select-optgroup")
      ),
      ...optgroupEls,
    ].sort(sortByAppearanceInDomTree)
  }

  private onHeaderClick(e: MouseEvent): void {
    const targetEl = e.target as HTMLElement
    const didClickOnSdxInputEl = !!closest(targetEl, this.filterInputEl!)

    if (
      this.isFilterable() &&
      this.isOpenOrOpening() &&
      didClickOnSdxInputEl &&
      !this.hadFilterInputElFocus
    ) {
      // Don't toggle when the user clicks on the filter input field
    } else {
      this.toggle()
    }

    this.hadFilterInputElFocus = this.hasFilterInputElFocus()
  }

  private onAutocompleteFocus(): void {
    this.invokeFocusCallback()
  }

  private onAutocompleteBlur(): void {
    this.invokeBlurCallback()
  }

  private onAutocompleteInput(e: Event): void {
    const value = (e.target as HTMLSdxInputElement).value

    this.store.set("filterInputElValue", value)
    this.store.dispatch({ type: "SET_FILTER", filter: value })

    // Debounced
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }

    this.resizeTimer = setTimeout(() => {
      if (isFilterLongEnough(value, this.filterMinLength)) {
        this.open()
      } else {
        this.close()
      }
    }, this.state.get().animationDuration) // can be controlled using animated="false"
  }

  /**
   * True if this sdx-select is filterable using a filter input field.
   */
  private isFilterable(): boolean {
    const state = this.state.get()

    return (
      isKeyboardBehavior(state, "filter") ||
      isKeyboardBehavior(state, "autocomplete")
    )
  }

  private hasVisibleOptionEls(): boolean {
    return this.state
      .get()
      .optionEls.some((optioneEl) => optioneEl.style.display !== "none")
  }

  private isOpenOrOpening(): boolean {
    const { display } = this.state.get()
    return display === "open" || display === "opening"
  }

  private isClosedOrClosing(): boolean {
    const { display } = this.state.get()
    return display === "closed" || display === "closing"
  }

  private isListOverlapping(): boolean {
    const { listEl } = this.state.get()

    if (!(listEl && this.wrapperEl)) {
      return false
    }

    return listEl.clientWidth > this.wrapperEl.clientWidth
  }

  /**
   * Normalizes max-height prop, e.g.:
   * 200 => "200px"
   * "50vh" => "50vh"
   */
  private getMaxHeight(): string | undefined {
    // No max-height prop given
    if (!this.maxHeight) {
      return
    }

    // If number, add "px"
    if (Number(this.maxHeight)) {
      return `${this.maxHeight}px`
    }

    // Unit is already given
    return String(this.maxHeight) // TS bug? String() shouldn't be needed because of above Number()
  }

  /**
   * Decide if element is inside a dialog and set state accordingly.
   * @returns The updated value of isInsideDialog.
   */
  private setIsInsideDialog() {
    const isInsideDialog =
      (!bp("sm") || isTouchscreen()) && this.keyboardBehavior !== "autocomplete"

    this.store.set("isInsideDialog", isInsideDialog)

    return isInsideDialog
  }

  private hasFilterInputElFocus() {
    if (isKeyboardBehavior(this.state.get(), "autocomplete")) {
      return this.filterInputEl === this.el.shadowRoot!.activeElement
    } else {
      return !!this.state.get().listEl?.shadowRoot!.activeElement
    }
  }

  private getDefaultInputFieldProps() {
    return {
      disabled: this.disabled,
      valid: this.valid,
      srHint: `${this.label} ${this.srHint} ${this.validationMessage || ""}`,
      required: this.required,
      role: "combobox",
      ariaExpanded: this.state.get().display === "open",
    }
  }

  private getComponentClassNames() {
    const { display, directionX, directionY } = this.state.get()

    return {
      component: true,
      focus: this.focussed,
      loading: this.loading,
      disabled: this.disabled,
      invalid: this.valid === false,
      filterable: this.isFilterable(),
      autocomplete: isKeyboardBehavior(this.state.get(), "autocomplete"),
      "is-list-overlapping": this.isListOverlapping(),
      [this.backgroundTheme]: true,
      [getAppearance(this.el)]: true,
      [display]: true,
      [directionX]: true,
      [directionY]: true,
    }
  }

  private getLabelClassNames() {
    return {
      label: true,
      [getAppearance(this.el)]: true,
    }
  }

  private getDefaultInputFieldStyle() {
    const state = this.state.get()
    const { isInsideDialog } = state

    const notClosed = state.display !== "closed"
    const openTowardsTop = state.directionY === "top"
    const openTowardsBottom = state.directionY === "bottom"
    const notClosedTowardsTop = notClosed && openTowardsTop && !isInsideDialog
    const notClosedTowardsBottom =
      notClosed && openTowardsBottom && !isInsideDialog

    // Provide enough space for chevron or loading spinner
    const paddingRight =
      isKeyboardBehavior(state, "autocomplete") && !this.loading
        ? undefined
        : "40px"

    return {
      paddingRight,
      borderTopLeftRadius: notClosedTowardsTop ? "0" : "",
      borderTopRightRadius: notClosedTowardsTop ? "0" : "",
      borderBottomLeftRadius: notClosedTowardsBottom ? "0" : "",
      borderBottomRightRadius: notClosedTowardsBottom ? "0" : "",
    }
  }

  private getAnimationDuration(): number {
    const { isInsideDialog, animationDuration } = this.state.get()
    return isInsideDialog ? 0 : animationDuration
  }

  public render() {
    const state = this.state.get()
    const appearance = getAppearance(this.el)

    return (
      <Host aria-expanded={(state.display === "open").toString()}>
        <div
          class={this.getComponentClassNames()}
          ref={(el) => (this.componentEl = el)}
        >
          {!this.isChildOfSdxFilter && this.label && (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <label
              class={this.getLabelClassNames()}
              onClick={() => this.toggle()}
            >
              {this.label}
              {this.required && <span aria-hidden="true">&nbsp;*</span>}
            </label>
          )}

          <div class="wrapper" ref={(el) => (this.wrapperEl = el)}>
            <div class="header-wrapper">
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div class="header" onClick={(e) => this.onHeaderClick(e)}>
                <div class="selection">
                  {isKeyboardBehavior(state, "autocomplete") ? (
                    <sdx-input
                      {...this.getDefaultInputFieldProps()}
                      class="autocomplete-input"
                      value={state.filterInputElValue}
                      placeholder={this.placeholder}
                      maxlength={this.maxlength}
                      editable={true}
                      type={this.loading ? undefined : this.type}
                      inputStyle={this.getDefaultInputFieldStyle()}
                      onInput={(e) => this.onAutocompleteInput(e)}
                      onFocus={() => this.onAutocompleteFocus()}
                      onBlur={() => this.onAutocompleteBlur()}
                      aria-autocomplete="list"
                      ref={(el) => (this.filterInputEl = el)}
                    />
                  ) : (
                    <sdx-input
                      {...this.getDefaultInputFieldProps()}
                      value={
                        (this.isChildOfSdxFilter && this.label) ||
                        getFormattedSelection(state.selection)
                      }
                      placeholder={this.placeholder}
                      badge={
                        this.multiple && state.selection.length
                          ? state.selection.length.toString()
                          : undefined
                      }
                      ref={(el) =>
                        (this.nonEditableInputEl = el as HTMLSdxInputElement)
                      }
                      editable={false}
                      valid={this.valid}
                      inputStyle={{
                        ...this.getDefaultInputFieldStyle(),
                        borderColor: this.isOpenOrOpening()
                          ? appearance === "sdx--light-theme"
                            ? "#086adb" // $color-int-blue
                            : "#4294FF" // $color-blue-int-3-dark-theme
                          : "",
                        color: this.isOpenOrOpening()
                          ? appearance === "sdx--light-theme"
                            ? "#1781e3" // $color-azure
                            : "#4294FF" // $color-blue-int-3-dark-theme
                          : "",
                      }}
                    />
                  )}
                </div>

                {/*
                  Don't show the thumb when "autocomplete" is set, because it should
                  look like an input field (except for when "loading" is set)
                */}
                {(!isKeyboardBehavior(state, "autocomplete") ||
                  this.loading) && (
                  <div class="thumb" aria-live="polite">
                    {this.loading ? (
                      <sdx-loading-spinner
                        srHint={this.srHint ? this.srHint : undefined}
                        strokeInherit
                      />
                    ) : (
                      <div class="icon"></div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {this.hasConsumerProvidedListEl ? (
              <slot />
            ) : (
              <sdx-select-list>
                <slot />
              </sdx-select-list>
            )}
          </div>

          {this.validationMessage && (
            <sdx-validation-message
              validationMessage={this.validationMessage}
            />
          )}
        </div>

        <sdx-dialog
          ref={(el) => (this.dialogEl = el)}
          label={this.label}
          type="closable-modal"
          animated={this.animated}
          onSdxdisplaychange={(e) => {
            if (e.detail?.display === "closing") {
              // needed because closing the dialog triggers disconnect
              // of sdx-select-option
              this.store.set("reappending", true)
            }

            if (e.detail?.display === "closed") {
              this.close()
            }
          }}
        >
          <sdx-dialog-content
            fixedHeight={isKeyboardBehavior(state, "filter")}
            ref={(el) => (this.dialogContentEl = el)}
          />
        </sdx-dialog>
      </Host>
    )
  }
}
